import { resolveId } from '@medplum/core'
import type { MedicationRequest, Reference } from '@medplum/fhirtypes'
import { formatDateNumber } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Badge, cn, DotSeparator } from '@seen/ui'
import type { JSX } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { MedicationHistoryTooltip } from '#src/components/medications/MedicationHistoryTooltip.tsx'
import { useRead } from '#src/hooks/useRead.ts'

const ORDER_PREFIX_RE = /^(New|Doc'd Change|D\/C|Refill|Reorder|Discontinue)\s+/

export function MedicationMention({
  medication: medicationReference,
  className,
  displayOverride,
}: {
  medication: Reference<MedicationRequest>
  className?: string
  displayOverride?: string | null
}): JSX.Element {
  const medicationId = resolveId(medicationReference)
  const { data: medication } = useRead(
    ExtendedMedicationRequest,
    medicationReference
  )
  const participantId = resolveId(medication?.subject)
  const medicationUrl =
    participantId && medicationId
      ? `/Patient/${participantId}/medications/${medicationId}`
      : undefined

  const prefixMatch = displayOverride
    ? ORDER_PREFIX_RE.exec(displayOverride)
    : null
  const prefix = prefixMatch?.[1]
  const displayText = displayOverride
    ? prefixMatch
      ? displayOverride.slice(prefixMatch[0].length)
      : displayOverride
    : (medication?.medicationDisplay ?? referenceDisplay(medicationReference))

  return (
    <HoverCard openDelay={300} closeDelay={200}>
      <HoverCardTrigger asChild>
        <span className="inline">
          <Link
            to={medicationUrl ?? '#'}
            className={cn(
              'select-none rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px hover:bg-gray-460 group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)] [&&]:no-underline',
              className
            )}
            tabIndex={-1}
            aria-disabled={!medicationUrl}
          >
            {prefix && (
              <Badge
                variant="outline"
                className="mr-1 inline-flex items-center align-[7%]!"
              >
                {prefix}
              </Badge>
            )}
            <span>{displayText}</span>
          </Link>
          {medication?.authoredOn && (
            <span className="text-foreground-tertiary select-none">
              <DotSeparator />
              Ordered {formatDateNumber(medication.authoredOn)}
            </span>
          )}
        </span>
      </HoverCardTrigger>
      {medication && (
        <HoverCardContent side="bottom" align="start" className="w-auto p-0">
          <MedicationHistoryTooltip
            medicationDisplay={medication.medicationDisplay ?? 'Medication'}
            medicationRequests={[medication]}
          />
        </HoverCardContent>
      )}
    </HoverCard>
  )
}
